import OsbUtilService from '../osb-util-service/osb-util-service';
import { SERVICE_LOCATION_STEP_KEYS } from '../../../components/sections/owners-osb/osb-constant';
import serverSideService from '../../server-side-service/server-side-service';
import HttpService from '../../http-service/http-service';
import {
    OSB_API_PATH,
    OSB_BASE_URL,
    GOOGLE_MAPS_SCRIPT_URL,
} from '../../../constants';
import { WorkingHours } from '../../../models/osb-model/osb-dealer-info';
declare global {
    interface Window {
        processDealerResults: any;
    }
}

const MAX_BATCH_COUNT = 25;

type NukleusDealerResult = {
    DealerID?: string;
    DealerName?: string;
    PostCode?: string;
    AddressLine1?: string;
    Locality?: string;
    CustomerRatingsService?: string | number;
    CustomerRespondentsService?: string | number;
    PrimaryPhone?: string;
    Latitude?: string | number;
    Longitude?: string | number;
    VideoCheck?: boolean;
    MobileService?: boolean;
    HasServiceDepartmentCV?: boolean;
    TransitService?: boolean;
    HasServiceDepartmentPV?: boolean;
    BEVService?: boolean;
    Distance?: string | number;
    ServiceMondayOpenTime?: string;
    ServiceMondayCloseTime?: string;
    ServiceTuesdayOpenTime?: string;
    ServiceTuesdayCloseTime?: string;
    ServiceWednesdayOpenTime?: string;
    ServiceWednesdayCloseTime?: string;
    ServiceThursdayOpenTime?: string;
    ServiceThursdayCloseTime?: string;
    ServiceFridayOpenTime?: string;
    ServiceFridayCloseTime?: string;
    ServiceSaturdayOpenTime?: string;
    ServiceSaturdayCloseTime?: string;
    ServiceSundayOpenTime?: string;
    ServiceSundayCloseTime?: string;
};

type NukleusDealersResponse = {
    dealers?: {
        results?: NukleusDealerResult[];
    };
};

export default class GoogleMapService {
    private static readonly getCurrentLocale = () => {
        return (
            OsbUtilService.getAppConfiguration().languageRegionCode || 'en-gb'
        ).toLowerCase();
    };

    private static readonly getCurrentCountryCode = () => {
        return OsbUtilService.getAppConfiguration().threeLetterCountryCode.toUpperCase();
    };

    private static readonly formatServiceTime = (time: string): string => {
        const normalizedTime = String(time || '').trim();
        if (!/^\d{4}$/.test(normalizedTime)) {
            return '';
        }

        return `${normalizedTime.substring(0, 2)}:${normalizedTime.substring(
            2,
            4
        )}`;
    };

    private static readonly getNukleusServiceHoursForDay = (
        dayKey: keyof WorkingHours,
        dealer: NukleusDealerResult
    ): string => {
        const capitalizedDay = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
        const openTime = GoogleMapService.formatServiceTime(
            String(
                dealer[
                    `Service${capitalizedDay}OpenTime` as keyof NukleusDealerResult
                ] || ''
            )
        );
        const closeTime = GoogleMapService.formatServiceTime(
            String(
                dealer[
                    `Service${capitalizedDay}CloseTime` as keyof NukleusDealerResult
                ] || ''
            )
        );

        if (!openTime || !closeTime) {
            return 'Closed';
        }

        return `${openTime} - ${closeTime}`;
    };

    private static readonly mapNukleusDealersToMapShape = (
        dealers: NukleusDealerResult[]
    ) => {
        return dealers.map(dealer => {
            const latitude = Number(dealer.Latitude);
            const longitude = Number(dealer.Longitude);
            const distanceKm = Number(dealer.Distance);
            const safeDistanceKm = Number.isFinite(distanceKm)
                ? distanceKm
                : null;

            return {
                DealerID: dealer.DealerID || '',
                DealerName: dealer.DealerName || '',
                PostCode: dealer.PostCode || '',
                AddressLine1: dealer.AddressLine1 || '',
                Locality: dealer.Locality || '',
                CustomerRatingsService: dealer.CustomerRatingsService || '',
                CustomerRespondentsService:
                    dealer.CustomerRespondentsService || '',
                PrimaryPhone: dealer.PrimaryPhone || '',
                location: {
                    lat: Number.isFinite(latitude) ? latitude : 0,
                    lng: Number.isFinite(longitude) ? longitude : 0,
                },
                distance:
                    typeof safeDistanceKm === 'number'
                        ? safeDistanceKm * 1000
                        : null,
                VideoCheck: Boolean(dealer.VideoCheck),
                MobileService: Boolean(dealer.MobileService),
                HasServiceDepartmentCV: Boolean(dealer.HasServiceDepartmentCV),
                TransitService: Boolean(dealer.TransitService),
                HasServiceDepartmentPV: Boolean(dealer.HasServiceDepartmentPV),
                BEVService: Boolean(dealer.BEVService),
                __Distance: safeDistanceKm,
                openingHours: {
                    monday: GoogleMapService.getNukleusServiceHoursForDay(
                        'monday',
                        dealer
                    ),
                    tuesday: GoogleMapService.getNukleusServiceHoursForDay(
                        'tuesday',
                        dealer
                    ),
                    wednesday: GoogleMapService.getNukleusServiceHoursForDay(
                        'wednesday',
                        dealer
                    ),
                    thursday: GoogleMapService.getNukleusServiceHoursForDay(
                        'thursday',
                        dealer
                    ),
                    friday: GoogleMapService.getNukleusServiceHoursForDay(
                        'friday',
                        dealer
                    ),
                    saturday: GoogleMapService.getNukleusServiceHoursForDay(
                        'saturday',
                        dealer
                    ),
                    sunday: GoogleMapService.getNukleusServiceHoursForDay(
                        'sunday',
                        dealer
                    ),
                },
            };
        });
    };

    private static readonly buildNukleusSearchUrl = (localeOverride = '') => {
        const locale = String(
            localeOverride || GoogleMapService.getCurrentLocale()
        ).toLowerCase();
        const resolvedPath = OSB_API_PATH.OSB_NUKLEUS_DEALERS.replace(
            ':locale',
            locale
        );
        const normalizedBaseUrl = String(OSB_BASE_URL || '').replace(
            /\/+$/,
            ''
        );
        const normalizedPath = String(resolvedPath || '').replace(/^\/+/, '');

        return `${normalizedBaseUrl}/${normalizedPath}`;
    };

    private static readonly applyMatchFilters = (
        dealers: any[],
        matchParameters: any
    ) => {
        if (!matchParameters) {
            return dealers;
        }

        const copiedMatchParameters = { ...matchParameters };
        delete copiedMatchParameters.CountryCode;

        const evaluateCondition = (dealer: any, key: string, value: any) => {
            const isNotEqual = key.indexOf('!') === 0;
            const actualKey = isNotEqual ? key.slice(1) : key;
            const dealerValue = String(dealer?.[actualKey] ?? '').trim();
            const expectedValue = String(value ?? '').trim();

            if (isNotEqual) {
                return dealerValue !== expectedValue;
            }
            return dealerValue === expectedValue;
        };

        const orClauses = copiedMatchParameters.OR_CLAUSES;
        delete copiedMatchParameters.OR_CLAUSES;

        return dealers.filter(dealer => {
            const andResult = Object.keys(copiedMatchParameters).every(key =>
                evaluateCondition(dealer, key, copiedMatchParameters[key])
            );

            if (!andResult) {
                return false;
            }

            if (!orClauses || !Array.isArray(orClauses) || !orClauses.length) {
                return true;
            }

            return orClauses.some((orClause: any) => {
                return Object.keys(orClause || {}).every(key =>
                    evaluateCondition(dealer, key, orClause[key])
                );
            });
        });
    };

    public static readonly loadScript = (url: string): void => {
        const scripts = Array.from(document.scripts);

        const isScriptAlreadyLoaded = (scriptUrl: string): boolean =>
            scripts.some(script => script.src === scriptUrl);

        const findScriptElement = (
            scriptUrl: string
        ): HTMLScriptElement | null =>
            scripts.find(script => script.src === scriptUrl) || null;

        const addScriptToDom = (scriptUrl: string): void => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptUrl;
            document.getElementsByTagName('head')[0].appendChild(script);
        };

        if (url === 'google') {
            if (serverSideService.isClientSide()) {
                const apiKey =
                    (window as any)?.SERVER_CONTEXT?.env
                        ?.REACT_APP_GOOGLE_MAPS_API_KEY ||
                    process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                const googleScriptUrl = GOOGLE_MAPS_SCRIPT_URL.replace(
                    ':apiKey',
                    apiKey || ''
                );
                const isGoogleLoaded =
                    typeof window.google === 'object' &&
                    typeof window.google.maps === 'object';

                if (
                    !isScriptAlreadyLoaded(googleScriptUrl) &&
                    !isGoogleLoaded
                ) {
                    addScriptToDom(googleScriptUrl);
                }
            }
        } else {
            const existingScriptElement = findScriptElement(url);
            if (existingScriptElement) {
                existingScriptElement.remove();
            }
            addScriptToDom(url);
        }
    };

    public static readonly getReverseGeoCoding = (
        lat: number,
        lng: number,
        callback: any
    ) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
            {
                location: new window.google.maps.LatLng(lat, lng),
                region:
                    OsbUtilService.getAppConfiguration().countryCode ===
                    'mothersite'
                        ? 'gb'
                        : OsbUtilService.getAppConfiguration().countryCode,
            },
            function(results) {
                if (results && results[0]) {
                    callback(results[0].formatted_address);
                }
            }
        );
    };

    public static readonly getPlacePredictions = (
        input: string,
        callback: any,
        geoCountryCode: any,
        hasPostcodeSearch: boolean
    ) => {
        let locationPredictions = [];
        let placesCompleted = 0;
        const places: any = {};
        const ac = new window.google.maps.places.AutocompleteService();
        let predictions: any[] = [];
        let postCodePredictions: any[] = [];
        const countryCodes: string[] = OsbUtilService.getGeoCountryCode(
            geoCountryCode
        ).split(',');
        for (const element of countryCodes) {
            const placesPredictionsCallback = (
                results: google.maps.places.AutocompletePrediction[] | null,
                status: google.maps.places.PlacesServiceStatus
            ) => {
                locationPredictions = [];
                if (
                    status ==
                        window.google.maps.places.PlacesServiceStatus.OK &&
                    results
                ) {
                    for (const element of results) {
                        locationPredictions.push(element);
                    }
                }
                places[element] = locationPredictions;
                placesCompleted++;

                if (placesCompleted === countryCodes.length) {
                    countryCodes.forEach(code => {
                        if (hasPostcodeSearch) {
                            postCodePredictions = places[code];
                            postCodePredictions.forEach(item => {
                                const hasPostalCodeItem =
                                    item?.types?.find(
                                        (type: string) =>
                                            type ===
                                            SERVICE_LOCATION_STEP_KEYS.SEARCH_PREDICTION_TYPE
                                    ) ===
                                    SERVICE_LOCATION_STEP_KEYS.SEARCH_PREDICTION_TYPE;
                                if (hasPostalCodeItem) {
                                    predictions.push(item);
                                }
                            });
                        } else {
                            predictions = predictions.concat(places[code]);
                        }
                    });
                    callback(predictions);
                }
            };
            ac.getPlacePredictions(
                {
                    input: input,
                    componentRestrictions: { country: element },
                    types: hasPostcodeSearch ? ['(regions)'] : ['geocode'],
                } as google.maps.places.AutocompletionRequest,
                placesPredictionsCallback
            );
        }
    };

    static getCityFromPostalCode(
        postalCode: string,
        countryCode: string,
        language = 'en'
    ): Promise<string | null> {
        return new Promise(resolve => {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
                {
                    address: postalCode,
                    region: language,
                    componentRestrictions: {
                        country: countryCode,
                    },
                },
                (results, status) => {
                    if (
                        status === window.google.maps.GeocoderStatus.OK &&
                        results
                    ) {
                        for (const component of results[0].address_components) {
                            if (
                                component.types.includes('political') ||
                                component.types.includes('locality') ||
                                component.types.includes('postal_town') ||
                                component.types.includes(
                                    'administrative_area_level_2'
                                ) ||
                                component.types.includes(
                                    'administrative_area_level_1'
                                )
                            ) {
                                resolve(component.long_name);
                                return;
                            }
                        }
                        resolve(results[0].formatted_address);
                    } else {
                        console.error('Geocoding failed :', status);
                        resolve(null);
                    }
                }
            );
        });
    }

    public static readonly getGeocodeLocation = (
        geoCountryCode: any,
        location: string,
        callback: any
    ) => {
        const geocoder = new window.google.maps.Geocoder();
        const countryCodes: string[] = OsbUtilService.getGeoCountryCode(
            geoCountryCode
        ).split(',');
        const geocodeResponse = (data: any) => {
            const locations = [];
            if (data) {
                let addLocation = false;
                for (let i = 0; i < data.length; i++) {
                    addLocation = false;
                    for (
                        let j = 0;
                        j < data[i].address_components.length;
                        j++
                    ) {
                        for (
                            let k = 0;
                            k < data[i].address_components[j].types.length;
                            k++
                        ) {
                            if (
                                data[i].address_components[j].types[k] ===
                                'country'
                            ) {
                                for (let l = 0; l < countryCodes.length; l++) {
                                    if (
                                        data[i].address_components[
                                            j
                                        ].short_name.toUpperCase() ===
                                        countryCodes[l].toUpperCase()
                                    ) {
                                        addLocation = true;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    if (addLocation) {
                        locations.push({
                            lat: data[i].geometry.location.lat(),
                            lng: data[i].geometry.location.lng(),
                            description: data[i].formatted_address,
                            types: data[i].types,
                            addressComponents: data[i].address_components,
                        });
                    }
                }
            }
            callback(locations);
        };
        const geocodeRequest: any = {
            address: location,
            region:
                OsbUtilService.getAppConfiguration().countryCode ===
                'mothersite'
                    ? 'gb'
                    : OsbUtilService.getAppConfiguration().countryCode,
        };
        // In the below code, added the language property to the geocoder options object,
        // which is then passed as the first argument to the geocoder.geocode() method.
        // This should force the Geocoding API to return the results in English, regardless of the user's location.
        // To fix the issue reported in Orange location in, France market.
        if (OsbUtilService.getAppConfiguration().countryCode === 'fr') {
            geocodeRequest.language = 'en-US';
        }
        geocoder.geocode(geocodeRequest, geocodeResponse);
    };
    public static readonly calculateDistanceUsingGooglePlaces = async (
        origin: google.maps.LatLng,
        destination: google.maps.LatLng
    ): Promise<number | null> => {
        return new Promise((resolve, reject) => {
            const directionsService = new google.maps.DirectionsService();
            const request: google.maps.DirectionsRequest = {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
            };

            directionsService.route(request, (response, status) => {
                if (
                    status === 'OK' &&
                    response?.routes?.[0]?.legs?.[0]?.distance?.text
                ) {
                    const distance = response.routes[0].legs[0].distance.value;
                    resolve(distance);
                } else {
                    console.error('Error calculating distance:', status);
                    reject(new Error('Failed to calculate distance'));
                }
            });
        });
    };
    public static readonly findDistanceBetweenAddresses = async (
        countryCode: string,
        address1: string,
        address2: string
    ): Promise<number | null> => {
        const getAddressGeocode = (
            address: string
        ): Promise<google.maps.LatLng> => {
            return new Promise<google.maps.LatLng>((resolve, reject) => {
                GoogleMapService.getGeocodeLocation(
                    countryCode,
                    address,
                    (geocode: any) => {
                        if (geocode && geocode.length > 0) {
                            const geocodeLatLng = new google.maps.LatLng(
                                geocode?.[0]?.lat,
                                geocode?.[0]?.lng
                            );
                            resolve(geocodeLatLng);
                        } else {
                            reject(
                                new Error(
                                    'Failed to get geocode for address: ' +
                                        address
                                )
                            );
                        }
                    }
                );
            });
        };
        try {
            const [address1Geocode, address2Geocode] = await Promise.all([
                getAddressGeocode(address1),
                getAddressGeocode(address2),
            ]);
            const distance = await GoogleMapService.calculateDistanceUsingGooglePlaces(
                address1Geocode,
                address2Geocode
            );
            return distance;
        } catch (error) {
            console.error('An error occurred:', error);
            return null;
        }
    };

    public static readonly getLocalisationConfig = (polygon: string) => {
        const polygons: { lng: string; lat: string }[] = [];
        polygon.split('%2C').forEach(item => {
            const latLng = item.match(/[^+]\d*\.\d*/g);
            if (latLng) {
                polygons.push({ lng: latLng[0], lat: latLng[1] });
            }
        });
        return polygons;
    };

    public static readonly getBoundsString = (polygon: string) => {
        const countryBounds = GoogleMapService.getLocalisationConfig(polygon);
        let boundsString = '';
        let nLat = -91; //North is +X, so maximise it in the range
        let sLat = 91; //South is -X, so minimise it in the range

        let eLng = -181; //East is +Y, so maximise it in the range
        let wLng = 181; //West is -Y, so minimise it in the range
        if (countryBounds) {
            for (let i = 0; i < countryBounds.length; i++) {
                const x = countryBounds[i];
                const cb = { lat: 0, lng: 0 };
                cb.lat = Number(x.lat);
                cb.lng = Number(x.lng);
                if (x.lat !== '' || x.lng !== '') {
                    if (cb.lat > nLat) {
                        nLat = cb.lat;
                    }
                    if (cb.lat < sLat) {
                        sLat = cb.lat;
                    }
                    if (cb.lng > eLng) {
                        eLng = cb.lng;
                    }
                    if (cb.lng < wLng) {
                        wLng = cb.lng;
                    }
                }
            }
            boundsString =
                '(' + sLat + ',' + wLng + ',' + nLat + ',' + eLng + ')';
        }
        return boundsString;
    };

    // DSL Nukleus dealer search
    public static readonly searchDealersByDistance = (
        origin: any,
        radius: number,
        limit: number,
        callback: any,
        matchParameters: any,
        polygon: string
    ) => {
        void polygon;
        const latitude = origin?.lat ?? origin?.latitude;
        const longitude = origin?.lng ?? origin?.longitude;

        if (latitude === undefined || longitude === undefined) {
            callback([]);
            return;
        }

        const parsedRadius = Number(radius);
        const normalizedRadius = Number.isFinite(parsedRadius)
            ? Math.max(1, Math.round(parsedRadius)).toString()
            : '1';

        const payload = {
            latitude: Number(latitude),
            longitude: Number(longitude),
            radius: normalizedRadius,
            unit: 'km',
            count: limit,
        };

        const requestHeaders = {
            ...HttpService.getAppIdRequestHeaders(),
            countryCode: GoogleMapService.getCurrentCountryCode(),
        };

        void HttpService.post<NukleusDealersResponse>(
            GoogleMapService.buildNukleusSearchUrl(),
            payload,
            { headers: requestHeaders }
        )
            .then(response => {
                const nukleusResults = response?.data?.dealers?.results || [];
                const mappedDealers = GoogleMapService.mapNukleusDealersToMapShape(
                    nukleusResults
                );
                const filteredDealers = GoogleMapService.applyMatchFilters(
                    mappedDealers,
                    matchParameters
                );
                callback(filteredDealers.slice(0, limit));
            })
            .catch(error => {
                console.error('DSL nukleus dealer search failed:', error);
                callback([]);
            });
    };

    // DSL Nukleus dealer lookup by dealerId
    public static readonly searchDealerById = (
        dealerId: string,
        callback: any,
        locale = ''
    ) => {
        const normalizedDealerId = String(dealerId || '').trim();

        if (!normalizedDealerId) {
            callback([]);
            return;
        }

        const requestHeaders = {
            ...HttpService.getAppIdRequestHeaders(),
            countryCode: GoogleMapService.getCurrentCountryCode(),
        };

        void HttpService.post<NukleusDealersResponse>(
            GoogleMapService.buildNukleusSearchUrl(locale),
            { dealerId: normalizedDealerId },
            { headers: requestHeaders }
        )
            .then(response => {
                const nukleusResults = response?.data?.dealers?.results || [];
                const mappedDealers = GoogleMapService.mapNukleusDealersToMapShape(
                    nukleusResults
                );
                callback(mappedDealers);
            })
            .catch(error => {
                console.error('DSL nukleus dealer search by id failed:', error);
                callback([]);
            });
    };

    public static readonly filterDealers = (
        dealers: any,
        containsParameters: any,
        maxResults: number
    ) => {
        if (!containsParameters) {
            return dealers;
        }

        const dealersFiltered = [];

        if (dealers.length > 0) {
            for (let i = 0; i < dealers.length; i++) {
                let allMatched = true;

                for (const key in containsParameters) {
                    if (containsParameters.hasOwnProperty.call(key)) {
                        const check = containsParameters[key];
                        const dealerString = dealers[i][key];

                        if (dealerString.indexOf(check) === -1) {
                            allMatched = false;
                        }
                    }
                }

                if (allMatched === true) {
                    dealersFiltered.push(dealers[i]);
                }
            }
        }

        let dealersReturned = [];

        if (dealersFiltered.length >= maxResults) {
            dealersReturned = dealersFiltered.slice(0, maxResults);
        } else {
            dealersReturned = dealersFiltered;
        }

        return dealersReturned;
    };

    public static readonly getDealerDistance = (
        origin: any,
        limit: any,
        dealers: string | any[],
        callback: (arg0: any) => void
    ) => {
        const service = new window.google.maps.DistanceMatrixService();
        const destinationLatLngs: any[] = [];
        const distanceMatrixDealers: any[] = [];

        //Create parallel array of lat lngs to pass into distance matrix
        for (let i = 0; i < dealers.length; i++) {
            destinationLatLngs.push(
                new window.google.maps.LatLng(
                    dealers[i].location.lat,
                    dealers[i].location.lng
                )
            );
        }

        //(Dealers chopped into) Chunks of 25, rounded up.
        //This is the have-I-finished array for AJAX calls.
        const distanceResponses = new Array(
            Math.ceil(destinationLatLngs.length / MAX_BATCH_COUNT)
        );
        if (dealers.length) {
            const createDistanceCallback = (index: number) => {
                return function(distances: any) {
                    const count = destinationLatLngs.slice(
                        index * MAX_BATCH_COUNT,
                        index * MAX_BATCH_COUNT + MAX_BATCH_COUNT
                    ).length;

                    if (distances) {
                        for (
                            let j = 0;
                            j < distances.rows[0].elements.length;
                            j++
                        ) {
                            if (distances.rows[0].elements[j].status === 'OK') {
                                dealers[index * MAX_BATCH_COUNT + j].distance =
                                    distances.rows[0].elements[
                                        j
                                    ].distance.value;
                                dealers[
                                    index * MAX_BATCH_COUNT + j
                                ].drivingTime =
                                    distances.rows[0].elements[
                                        j
                                    ].duration.value;
                            } else if (
                                dealers[index * MAX_BATCH_COUNT + j].__Distance
                            ) {
                                //Google's getDistanceMatrix, not providing distance, fall back to BING distance
                                dealers[index * MAX_BATCH_COUNT + j].distance =
                                    dealers[index * MAX_BATCH_COUNT + j]
                                        .__Distance * 1000;
                            } else {
                                //Error handling for no distance.
                                dealers[
                                    index * MAX_BATCH_COUNT + j
                                ].distanceError =
                                    distances.rows[0].elements[j].status;
                                dealers[
                                    index * MAX_BATCH_COUNT + j
                                ].distance = null;
                            }
                            distanceMatrixDealers.push(
                                dealers[index * MAX_BATCH_COUNT + j]
                            );
                        }
                    } else {
                        //Error is no distances array returned
                        for (let j = 0; j < count; j++) {
                            dealers[index * MAX_BATCH_COUNT + j].distanceError =
                                'DRIVING DISTANCE UNAVAILABLE';
                            dealers[
                                index * MAX_BATCH_COUNT + j
                            ].distance = null;
                            distanceMatrixDealers.push(
                                dealers[index * MAX_BATCH_COUNT + j]
                            );
                        }
                    }
                    //This chunk of dealers has come back.
                    distanceResponses[index] = true;
                    let responsesReturned = true;

                    //If any of the chunks haven't returned, we fail.
                    for (const response of distanceResponses) {
                        if (!response) {
                            responsesReturned = false;
                        }
                    }
                    if (responsesReturned) {
                        //The dealers array becomes our array of dealers that went through the distance matrix.
                        //This seems the dealers are in the right order regardless of function-call return order.
                        dealers = [...distanceMatrixDealers].sort(function(
                            a,
                            b
                        ) {
                            if (
                                typeof a.distance === 'number' &&
                                typeof b.distance === 'number'
                            ) {
                                return a.distance - b.distance;
                            } else {
                                if (
                                    typeof a.distance !== 'number' &&
                                    typeof b.distance !== 'number'
                                ) {
                                    return a.directDistance - b.directDistance;
                                }
                                if (typeof a.distance === 'number') {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            }
                        });
                        callback(dealers.slice(0, limit));
                    }
                };
            };
            for (
                let i = 0;
                i < destinationLatLngs.length / MAX_BATCH_COUNT;
                i++
            ) {
                service.getDistanceMatrix(
                    {
                        origins: [origin],
                        destinations: destinationLatLngs.slice(
                            i * MAX_BATCH_COUNT,
                            i * MAX_BATCH_COUNT + MAX_BATCH_COUNT
                        ),
                        travelMode: window.google.maps.TravelMode.DRIVING,
                    },
                    createDistanceCallback(i)
                );
            }
        } else {
            callback(dealers);
        }
    };
}
